import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import {
	ClassicEditor,
	AccessibilityHelp,
	Autoformat,
	Autosave,
	BlockQuote,
	Bold,
	Code,
	Essentials,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	Fullscreen,
	Heading,
	Highlight,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	LinkImage,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	List,
	Alignment,
	AutoLink,
	ListProperties,
	Paragraph,
	PasteFromOffice,
	SelectAll,
	SimpleUploadAdapter,
	Base64UploadAdapter,
	Strikethrough,
	SourceEditing,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo,
	GeneralHtmlSupport,
	type EditorConfig,
	Editor
} from 'ckeditor5';

@Component({
	selector: 'app-wysiwyg-editor',
	templateUrl: './wysiwyg-editor.component.html',
	styleUrls: ['./wysiwyg-editor.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class WysiwygEditorComponent implements OnInit, AfterViewInit, OnChanges {

	@Input() input: any = false;
	@Input() config: any = false;
	@Input() changed: any = false;
	@Input() inModal = false;
	/** Use full width and a taller edit area (e.g. statement letters). Default keeps legacy compact sizing. */
	@Input() layout: 'default' | 'full' = 'default';

	@Output() data = new EventEmitter<any>();
	formControlRef = new UntypedFormControl('');


	constructor(private cdr: ChangeDetectorRef) { }


	ngOnChanges(changes: SimpleChanges): void {
		if(changes.changed){
			this.init();
		}
	}

	ngOnInit(): void {
		this.formControlRef.valueChanges.subscribe((newV) => {
			this.data.emit(newV);
		})
	}

	public isLayoutReady = false;
	public Editor = ClassicEditor;
	public ckconfig: EditorConfig = {};

	ngAfterViewInit(): void {
		if (this.inModal) {
			setTimeout(() => this.init(), 150);
		} else {
			this.init();
		}
	}

	
	init(){
		this.getConfig(this.config)
		this.formControlRef.setValue(this.sanitizeEditorContent(this.input.value));
		this.formControlRef.updateValueAndValidity();
		this.isLayoutReady = true;
		this.cdr.detectChanges();
	}

	

	sanitizeEditorContent(rawHtml: string): string {
	  return rawHtml
			.replace(/style="margin-left:-?\d+pt;?"/g, '') // remove margin-left
			.replace(/<br[^>]*data-cke-filler[^>]*>/g, '') // remove filler <br>
			.replace(/\s?style=""/g, ''); // clean up empty style tags
	}

	getConfig(type) {
		switch (type) {
		case 'minimal':
			  this.ckconfig = {
				licenseKey: 'GPL',
				toolbar: {
				  items: ['bold', 'italic'],
				  shouldNotGroupWhenFull: true
				},
				plugins: [
				  Essentials,
				  Bold,
				  Italic,
				  Paragraph,
				  GeneralHtmlSupport
				],
				heading: {
				  options: [
						{
					  model: 'paragraph',
					  title: 'Paragraph',
					  class: 'ck-heading_paragraph'
						}
				  ]
				},
				placeholder: '',
				initialData: '',
				htmlSupport: {
					allow: [{
						name: 'iframe',
						attributes: true,
                    	classes: true,
                    	styles: true
					}]
				},
			  };
			  break;
		case 'limited':
			this.ckconfig = {
			    licenseKey: 'GPL',
				toolbar: {
					items: [
						'undo',
						'redo',
						'|',
						'heading',
						'|',
						'fontSize',
						'|',
						'bold',
						'italic',
						'underline',
						'|',
						'link',
						'blockQuote',
						'|',
						'alignment',
						'|',
						'bulletedList',
						'numberedList',
						'outdent',
						'indent',
						'fullscreen'
					],
					shouldNotGroupWhenFull: false,
				},
				plugins: [
					AccessibilityHelp,
					Alignment,
					AutoLink,
					Autosave,
					BlockQuote,
					Bold,
					Essentials,
					FontSize,
					Fullscreen,
					Heading,
					Indent,
					IndentBlock,
					Italic,
					Link,
					List,
					ListProperties,
					Paragraph,
					SelectAll,
					Underline,
					Undo,
					GeneralHtmlSupport
				],
				fontSize: {
					options: [10, 12, 14, 'default', 18, 20],
					supportAllValues: true
				},
				htmlSupport: {
					allow: [{
						name: 'iframe',
						attributes: true,
                    	classes: true,
                    	styles: true
					}]
				},
				heading: {
					options: [
						{
							model: 'paragraph',
							title: 'Paragraph',
							class: 'ck-heading_paragraph'
						},
						{
							model: 'heading1',
							view: 'h1',
							title: 'Heading 1',
							class: 'ck-heading_heading1'
						},
						{
							model: 'heading2',
							view: 'h2',
							title: 'Heading 2',
							class: 'ck-heading_heading2'
						},
						{
							model: 'heading3',
							view: 'h3',
							title: 'Heading 3',
							class: 'ck-heading_heading3'
						},
						{
							model: 'heading4',
							view: 'h4',
							title: 'Heading 4',
							class: 'ck-heading_heading4'
						},
						{
							model: 'heading5',
							view: 'h5',
							title: 'Heading 5',
							class: 'ck-heading_heading5'
						},
						{
							model: 'heading6',
							view: 'h6',
							title: 'Heading 6',
							class: 'ck-heading_heading6'
						}
					]
				},
				initialData: '',
				link: {
					addTargetToExternalLinks: true,
					defaultProtocol: 'https://',
					decorators: {
						toggleDownloadable: {
							mode: 'manual',
							label: 'Downloadable',
							attributes: {
								download: 'file'
							}
						}
					}
				},
				list: {
					properties: {
						styles: true,
						startIndex: true,
						reversed: true
					}
				},
				placeholder: ''
			};
			break;
		default:
			this.ckconfig = {
				licenseKey: 'GPL',
				toolbar: {
					items: [
						'undo',
						'redo',
						'|',
						'heading',
						'|',
						'fontSize',
						'fontFamily',
						'fontColor',
						'fontBackgroundColor',
						'|',
						'bold',
						'italic',
						'underline',
						'strikethrough',
						'code',
						'|',
						'link',
						'insertTable',
						'highlight',
						'blockQuote',
						'|',
						'bulletedList',
						'numberedList',
						'todoList',
						'outdent',
						'indent',
						'insertImage',
						'|',
						'sourceEditing',
						'fullscreen',
					],
					shouldNotGroupWhenFull: true
				},
				plugins: [
					AccessibilityHelp,
					Autoformat,
					Autosave,
					BlockQuote,
					Bold,
					Code,
					Essentials,
					FontBackgroundColor,
					FontColor,
					FontFamily,
					FontSize,
					Fullscreen,
					Heading,
					Highlight,
					Indent,
					IndentBlock,
					Italic,
					Image,
					ImageCaption,
					ImageResize,
					ImageStyle,
					ImageToolbar,
					ImageUpload,
					LinkImage,
					Link,
					List,
					ListProperties,
					Paragraph,
					PasteFromOffice,
					SelectAll,
					Strikethrough,
					SourceEditing,
					Base64UploadAdapter,
					Table,
					TableCaption,
					TableCellProperties,
					TableColumnResize,
					TableProperties,
					TableToolbar,
					TextTransformation,
					TodoList,
					Underline,
					Undo,
					GeneralHtmlSupport
				],
				fontFamily: {
					supportAllValues: true
				},
				fontSize: {
					options: [10, 12, 14, 'default', 18, 20, 22],
					supportAllValues: true
				},
				heading: {
					options: [
						{
							model: 'paragraph',
							title: 'Paragraph',
							class: 'ck-heading_paragraph'
						},
						{
							model: 'heading1',
							view: 'h1',
							title: 'Heading 1',
							class: 'ck-heading_heading1'
						},
						{
							model: 'heading2',
							view: 'h2',
							title: 'Heading 2',
							class: 'ck-heading_heading2'
						},
						{
							model: 'heading3',
							view: 'h3',
							title: 'Heading 3',
							class: 'ck-heading_heading3'
						},
						{
							model: 'heading4',
							view: 'h4',
							title: 'Heading 4',
							class: 'ck-heading_heading4'
						},
						{
							model: 'heading5',
							view: 'h5',
							title: 'Heading 5',
							class: 'ck-heading_heading5'
						},
						{
							model: 'heading6',
							view: 'h6',
							title: 'Heading 6',
							class: 'ck-heading_heading6'
						}
					]
				},
				initialData: '',
				link: {
					addTargetToExternalLinks: true,
					defaultProtocol: 'https://',
					decorators: {
						toggleDownloadable: {
							mode: 'manual',
							label: 'Downloadable',
							attributes: {
								download: 'file'
							}
						}
					}
				},
				list: {
					properties: {
						styles: true,
						startIndex: true,
						reversed: true
					}
				},
				placeholder: '',
				table: {
					contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
				},
				htmlSupport: {
					allow: [{
						name: /.*/,
						attributes: true,
                    	classes: true,
                    	styles: true
					}]
				},
			};
			break;
		}
	}





}
